import JSZip from 'jszip'
import DiffReviewPanel from '~/components/task/DiffReviewPanel'
import { ModalId, useModalController } from '~/hooks/useModal'
import { Diff } from '~/protos/agents'
import { getDiffContent, getDiffPath } from '~/utils/diffUtils'
import { BetterToast } from '~/utils/toast'
import { EmptyState } from '../common'

interface RefactorFileViewerSlideProps {
  diffs: Diff[]
  onClose?: () => void
  initialActivePath?: string
}

export function RefactorFileViewerSlide({
  diffs,
  onClose,
  initialActivePath
}: RefactorFileViewerSlideProps) {
  const { closeModal } = useModalController(ModalId.RefactorFileViewer)
  const hasDiffs = Array.isArray(diffs) && diffs.length > 0

  const downloadAllFiles = async () => {
    if (!hasDiffs) return

    try {
      const zip = new JSZip()

      for (const diff of diffs) {
        const filePath = getDiffPath(diff)
        const content = getDiffContent(diff)
        const pathSegments = filePath.split('/')
        const fileName = pathSegments.pop()
        let currentFolder = zip

        for (const segment of pathSegments) {
          currentFolder = currentFolder.folder(segment) ?? currentFolder
        }

        if (fileName) currentFolder.file(fileName, content)
      }

      const content = await zip.generateAsync({ type: 'blob' })
      const url = window.URL.createObjectURL(content)
      const link = document.createElement('a')
      link.href = url
      link.download = 'refactor-files.zip'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error creating zip file:', error)
      BetterToast.error('Failed to create zip file')
    }
  }

  return (
    <div className='flex h-[900px] w-[1400px] flex-col overflow-hidden rounded-[28px] bg-white shadow-[0px_6px_16px_0px_rgba(0,0,0,0.08),0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]'>
      <div className='flex h-[72px] shrink-0 items-center justify-between border-b border-tokens-neutral-border-2 px-6'>
        <div className='min-w-0'>
          <p className='truncate font-geist text-sm font-semibold leading-[22px] text-tokens-neutral-text-primary'>
            Generated files
          </p>
          <p className='text-xs text-tokens-neutral-text-secondary'>
            {hasDiffs ? `${diffs.length} file${diffs.length === 1 ? '' : 's'}` : 'No files'}
          </p>
        </div>
        <div className='ml-4 flex shrink-0 items-center gap-2'>
          <button
            type='button'
            onClick={downloadAllFiles}
            disabled={!hasDiffs}
            className='flex h-9 items-center gap-2 rounded-xl border border-tokens-neutral-border-1 px-4 py-2 transition-colors hover:bg-tokens-neutral-fill-fill-6 disabled:cursor-not-allowed disabled:opacity-50'
          >
            <span className='font-geist text-sm font-medium leading-[22px] text-tokens-neutral-text-primary'>
              Download
            </span>
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M8 2v8m0 0l3-3m-3 3L5 7'
                stroke='#8E8E8E'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1'
                stroke='#8E8E8E'
                strokeWidth='1.5'
                strokeLinecap='round'
              />
            </svg>
          </button>
          <button
            type='button'
            onClick={onClose || closeModal}
            className='flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-tokens-neutral-fill-fill-6'
            aria-label='Close'
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M18 6L6 18M6 6l12 12'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      </div>

      <div className='min-h-0 flex-1 overflow-hidden bg-tokens-neutral-bg-grey-2 p-4'>
        {hasDiffs ? (
          <DiffReviewPanel diffs={diffs} fillHeight initialActivePath={initialActivePath} />
        ) : (
          <EmptyState
            message='No documentation changes available'
            icon='📄'
            description='The generated documentation may not contain code changes to review'
            className='h-full w-full'
          />
        )}
      </div>
    </div>
  )
}
